import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";
import { useLiveChat } from "../../contexts/LiveChatContext";
import { hasSectionAccess } from "../../utils/sectionAccess";
import { getCurrentUser } from "../../apis/auth";
import { isSuperAdmin, logout } from "../../utils/auth";
import useSubscriptionAccess from "../../hooks/useSubscriptionAccess";
import SubscribeRequiredPrompt from "../SubscribeRequiredPrompt";
import { logEvent } from "../../apis/events";
import { toast } from "react-toastify";
import BrandLogo from "../BrandLogo";
import {
  ICONS,
  IconHome,
  IconChat,
  IconMega,
  IconBag,
  IconChart,
  IconGear,
  IconSwap,
  IconLogout,
  IconGrid,
  IconBack,
} from "../../ui/ping/icons";
import PhoneProfileAvatar from "../whatsapp/PhoneProfileAvatar";
import { formatMessagingLimit, formatQualityRating, qualityTone } from "../../utils/phoneHealthLabels";

const RAIL = [
  { id: "home", title: "Home", def: "/dashboard", Icon: IconHome },
  { id: "chat", title: "Chat", def: "/live-chats", Icon: IconChat },
  { id: "broadcast", title: "Broadcast", def: "/broadcast", Icon: IconMega },
  { id: "commerce", title: "Commerce", def: "/commerce?tab=orders", Icon: IconBag },
  { id: "insights", title: "Insights", def: "/dashboard", Icon: IconChart },
];

// Pages that stay visible when the account has no subscription:
// dashboard, connecting a number, billing/profile basics and the plans page.
const UNSUBSCRIBED_PATHS = new Set([
  "/dashboard",
  "/waba",
  "/subscribe",
  "/profile",
  "/wallet",
  "/invoices",
]);

function buildGroups() {
  return {
    home: [
      { type: "label", text: "ENGAGE" },
      { path: "/dashboard", label: "Dashboard", icon: "grid", section: "" },
      { path: "/waba", label: "Phone Numbers", icon: "phone", section: "phone_numbers" },
      { path: "/qr-codes", label: "QR Codes", icon: "qr", section: "phone_numbers" },
      { type: "label", text: "ACCOUNT" },
      { path: "/wallet", label: "Wallet", icon: "wallet", section: "", ownerOnly: true, role: "Owner" },
      { path: "/invoices", label: "Invoices", icon: "doc", section: "" },
      { path: "/api-keys", label: "API Keys", icon: "key", superAdminOnly: true, role: "Owner" },
      { path: "/api-docs", label: "API Docs", icon: "book", section: "" },
      { path: "/download-center", label: "Downloads", icon: "down", section: "downloads" },
    ],
    chat: [
      { type: "label", text: "ENGAGE" },
      { path: "/live-chats", label: "Live chat", icon: "chat", section: ["livechats", "livechats.own", "livechats.all"], badge: "unread" },
      { path: "/contacts?tab=contacts", label: "Contacts", icon: "people", section: "contacts_management" },
      { path: "/chat-statuses", label: "Chat Statuses", icon: "tag", section: "agents" },
      { path: "/agents", label: "Team", icon: "people", section: "agents" },
      { path: "/agent-analysis", label: "Agent Analysis", icon: "chart", section: "agents" },
    ],
    broadcast: [
      { type: "label", text: "ENGAGE" },
      { path: "/broadcast", label: "Campaigns", icon: "mega", section: ["broadcasting", "campaigns"] },
      { path: "/templates", label: "Templates", icon: "list", section: "templates" },
      { path: "/flow-list", label: "Flows", icon: "bot", section: "flows" },
      { path: "/whatsapp-flows", label: "WA Flows", icon: "form", section: "flows" },
    ],
    commerce: [
      { type: "label", text: "SELL" },
      // Catalog tab hidden until Meta Manage-catalogue attach works.
      // { path: "/commerce?tab=catalog", label: "Catalog", icon: "bag", section: "phone_numbers" },
      { path: "/commerce?tab=orders", label: "Orders", icon: "order", section: "phone_numbers" },
      { path: "/whatsapp-payments", label: "Payments", icon: "pay", section: "phone_numbers" },
    ],
    insights: [
      { type: "label", text: "INSIGHTS" },
      { path: "/dashboard", label: "Overview", icon: "chart", section: "" },
      { path: "/agent-analysis", label: "Agent Analysis", icon: "people", section: "agents" },
      { path: "/contacts?tab=dashboard", label: "Contacts Dashboard", icon: "grid", section: "contacts_management" },
    ],
    settings: [
      { type: "label", text: "ACCOUNT" },
      { path: "/settings", label: "Settings", icon: "gear", section: "settings" },
    ],
    profile: [
      { type: "label", text: "ACCOUNT" },
      { path: "/profile", label: "Profile", icon: "user", section: "profile" },
      { path: "/wallet", label: "Wallet", icon: "wallet", section: "", ownerOnly: true, role: "Owner" },
      { type: "logout" },
    ],
  };
}

function itemMatches(pathname, search, item) {
  if (!item.path) return false;
  const [p, q] = item.path.split("?");
  if (p === "/broadcast" && (pathname === "/broadcast" || pathname.startsWith("/campaigns"))) return !q;
  if (p === "/commerce") {
    if (pathname !== "/commerce") return false;
    const tab = new URLSearchParams(search).get("tab") || "orders";
    const want = new URLSearchParams(q || "tab=orders").get("tab") || "orders";
    return tab === want;
  }
  if (p === "/contacts") {
    if (pathname !== "/contacts") return false;
    const tab = new URLSearchParams(search).get("tab") || "dashboard";
    const want = q ? new URLSearchParams(q).get("tab") : null;
    if (want === "dashboard") return tab === "dashboard";
    if (want === "contacts") return tab !== "dashboard";
    return true;
  }
  if (p === "/dashboard") return pathname === "/dashboard";
  if (p === "/waba") return pathname === "/waba" || pathname.startsWith("/waba/");
  if (p === "/templates") return pathname.startsWith("/templates");
  if (p === "/flow-list") return pathname === "/flow-list" || pathname === "/flows" || pathname.startsWith("/flow-builder") || pathname.startsWith("/flows/builder");
  return pathname === p || pathname.startsWith(`${p}/`);
}

function groupForLocation(pathname, search, preferred) {
  if (pathname === "/settings") return "settings";
  if (pathname === "/profile") return "profile";
  if (pathname === "/live-chats" || pathname === "/chat-statuses" || pathname === "/agents") return "chat";
  if (pathname === "/agent-analysis") return preferred === "chat" ? "chat" : "insights";
  if (pathname === "/contacts") {
    const tab = new URLSearchParams(search).get("tab");
    if (tab === "dashboard") return "insights";
    return "chat";
  }
  if (pathname === "/broadcast" || pathname.startsWith("/campaigns") || pathname.startsWith("/templates") || pathname === "/flow-list" || pathname === "/flows" || pathname.startsWith("/flow-builder") || pathname.startsWith("/flows/builder") || pathname === "/whatsapp-flows") {
    return "broadcast";
  }
  if (pathname === "/commerce" || pathname === "/whatsapp-payments") return "commerce";
  if (pathname === "/dashboard") return preferred === "insights" ? "insights" : "home";
  if (
    pathname === "/wallet" ||
    pathname === "/invoices" ||
    pathname === "/api-keys" ||
    pathname === "/api-docs" ||
    pathname === "/download-center" ||
    pathname === "/qr-codes" ||
    pathname === "/waba" ||
    pathname.startsWith("/waba/")
  ) {
    return "home";
  }
  return preferred || "home";
}

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "BP";

const qualityChipTone = (rating) => {
  const t = qualityTone(rating);
  if (t === "good") return "g";
  if (t === "warn") return "a";
  if (t === "bad") return "r";
  return "n";
};

const phoneIdFromPath = (pathname) => pathname.match(/^\/waba\/([^/]+)/)?.[1] || null;

const AppNav = ({ panelOpen, setPanelOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { phoneAccounts, selectedPhoneId, setSelectedPhoneId, selectedPhoneAccount, fetchPhoneAccounts } =
    usePhoneAccount();
  const liveChat = useLiveChat();
  const [acctOpen, setAcctOpen] = useState(false);
  const [navReady, setNavReady] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1080 : true
  );
  const [chatFlyoutOpen, setChatFlyoutOpen] = useState(false);
  const [chatMenuArmed, setChatMenuArmed] = useState(false);
  const [preferredGroup, setPreferredGroup] = useState(null);
  const [editorOpen, setEditorOpen] = useState(
    () => typeof document !== "undefined" && document.documentElement.hasAttribute("data-ping-collapse-nav")
  );
  const acctRef = useRef(null);
  const flyoutTimer = useRef(null);

  const [userSections, setUserSections] = useState(() => {
    try {
      const s = localStorage.getItem("sections");
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [agentPermissions, setAgentPermissions] = useState(() => {
    try {
      const p = localStorage.getItem("agentPermissions");
      return p ? JSON.parse(p) : [];
    } catch {
      return [];
    }
  });
  const [userInitial, setUserInitial] = useState("U");
  const [subscribePromptOpen, setSubscribePromptOpen] = useState(false);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      if (u.name) setUserInitial(u.name.charAt(0).toUpperCase());
    } catch {
      /* ignore */
    }
    getCurrentUser()
      .then((res) => {
        const fresh = res?.data?.sections;
        const perms = res?.data?.permissions;
        if (Array.isArray(fresh)) {
          localStorage.setItem("sections", JSON.stringify(fresh));
          setUserSections(fresh);
        }
        if (Array.isArray(perms)) {
          localStorage.setItem("agentPermissions", JSON.stringify(perms));
          setAgentPermissions(perms);
        }
        const name = res?.data?.name || res?.data?.user?.name;
        if (name) setUserInitial(name.charAt(0).toUpperCase());
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchPhoneAccounts();
  }, [fetchPhoneAccounts]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setNavReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false);
    };
    if (acctOpen) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [acctOpen]);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1080);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onLiveChats =
    location.pathname === "/live-chats" || location.pathname.startsWith("/live-chats/");
  const groupId = groupForLocation(location.pathname, location.search, preferredGroup);
  const chatHoverReady = chatMenuArmed || groupId === "chat";
  const panelFlyoutMode = isDesktop && (onLiveChats || editorOpen);
  const flyoutReady = chatHoverReady || editorOpen;

  useEffect(() => {
    const sync = () =>
      setEditorOpen(document.documentElement.hasAttribute("data-ping-collapse-nav"));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-ping-collapse-nav"],
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (groupId === "chat") {
      setChatMenuArmed(true);
      return;
    }
    if (!onLiveChats) {
      setChatFlyoutOpen(false);
      setChatMenuArmed(false);
    }
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
  }, [location.pathname, location.search, preferredGroup, groupId, onLiveChats]);

  useEffect(() => () => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
  }, []);

  const openChatFlyout = () => {
    if (!flyoutReady) return;
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    setChatFlyoutOpen(true);
  };

  const closeChatFlyout = () => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    flyoutTimer.current = setTimeout(() => setChatFlyoutOpen(false), 140);
  };

  const canAccess = useCallback(
    (sectionOrSections) => hasSectionAccess(sectionOrSections, userSections, agentPermissions),
    [userSections, agentPermissions]
  );

  const owner = isSuperAdmin();
  const groups = useMemo(() => buildGroups(), []);
  const { loading: accessLoading, hasAccess } = useSubscriptionAccess();
  const gated = !accessLoading && !hasAccess;

  const visibleItems = useMemo(() => {
    let items = groups[chatFlyoutOpen && !editorOpen ? "chat" : groupId] || groups.home;
    if (gated) {
      items = items.filter((item) => {
        if (item.type === "label" || item.type === "logout") return true;
        const base = (item.path || "").split("?")[0];
        return UNSUBSCRIBED_PATHS.has(base);
      });
      items = [
        { path: "/subscribe", label: "Subscribe", icon: "pay", section: "", highlight: true },
        ...items,
      ];
    }
    return items.filter((item) => {
      if (item.type === "label") return true;
      if (item.type === "logout") return true;
      if (item.superAdminOnly && !owner) return false;
      if (item.ownerOnly && !owner) return false;
      if (!item.section) return true;
      if (owner) return true;
      return canAccess(item.section);
    }).filter((item, i, arr) => {
      if (item.type !== "label") return true;
      const next = arr.slice(i + 1).find((x) => x.type !== "label");
      return next && next.type !== "label";
    });
  }, [groups, groupId, chatFlyoutOpen, editorOpen, owner, canAccess, gated]);

  const unread = useMemo(() => {
    const chats = liveChat?.chats || [];
    return chats.reduce((n, c) => n + (Number(c.unread) || 0), 0);
  }, [liveChat?.chats]);

  const selected = selectedPhoneAccount || phoneAccounts.find((a) => a.phoneId === selectedPhoneId);
  const displayName = selected?.phoneName || selected?.wabaName || "Select number";
  const displayNumber = selected?.phoneNumber || "No WhatsApp connected";
  const phoneDetailId = phoneIdFromPath(location.pathname);
  const phonesOpen = Boolean(phoneDetailId);

  useEffect(() => {
    if (phoneDetailId && phoneDetailId !== selectedPhoneId) {
      setSelectedPhoneId(phoneDetailId);
    }
  }, [phoneDetailId, selectedPhoneId, setSelectedPhoneId]);

  const selectNumber = (acc, { closePanel = false } = {}) => {
    if (!acc?.phoneId) return;
    setSelectedPhoneId(acc.phoneId);
    window.dispatchEvent(new CustomEvent("wabaAccountChanged", { detail: acc }));
    if (location.pathname !== `/waba/${acc.phoneId}`) {
      navigate(`/waba/${acc.phoneId}`);
    }
    if (closePanel) setPanelOpen(false);
  };

  const openPhoneNumbers = (e) => {
    e?.preventDefault();
    const target =
      phoneAccounts.find((a) => a.phoneId === selectedPhoneId) || phoneAccounts[0];
    if (!target) {
      navigate("/waba");
      return;
    }
    selectNumber(target);
  };

  const addNumberFromPane = () => {
    logEvent("connect_wa_clicked", { source: "nav_numbers" });
    if (!accessLoading && !hasAccess) {
      toast.info("You don't have an active subscription. Subscribe to connect your WhatsApp number.");
      setSubscribePromptOpen(true);
      return;
    }
    setPanelOpen(false);
    navigate("/waba?connect=1");
  };

  const closePhoneNumbers = () => {
    navigate("/waba");
  };

  const goRail = (rail) => {
    setPreferredGroup(rail.id);
    if (rail.id === "chat") {
      if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
      setChatMenuArmed(true);
      setChatFlyoutOpen(true);
      setPanelOpen(true);
      if (!onLiveChats) navigate(rail.def);
      return;
    }
    if (rail.id === "broadcast" && editorOpen) {
      if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
      setChatFlyoutOpen(true);
      setPanelOpen(true);
      return;
    }
    setChatMenuArmed(false);
    setChatFlyoutOpen(false);
    navigate(rail.def);
    setPanelOpen(true);
  };

  return (
    <>
      <nav className="ping-rail" aria-label="Primary">
        <BrandLogo className="logo" />
        {(gated ? RAIL.filter((r) => r.id === "home") : RAIL).map((rail) => {
          const isChatRail = rail.id === "chat";
          const isBroadcastHost = rail.id === "broadcast" && editorOpen;
          const isFlyoutHost = isChatRail || isBroadcastHost;
          return (
            <button
              key={rail.id}
              type="button"
              className={`rail-btn${
                isChatRail
                  ? chatFlyoutOpen || groupId === "chat"
                    ? " on"
                    : ""
                  : !chatFlyoutOpen && groupId === rail.id
                    ? " on"
                    : ""
              }${isFlyoutHost ? " flyout-host" : ""}`}
              title={isChatRail || isBroadcastHost ? undefined : rail.title}
              aria-label={rail.title}
              aria-expanded={isFlyoutHost ? chatFlyoutOpen : undefined}
              aria-haspopup={isFlyoutHost ? "true" : undefined}
              onClick={() => goRail(rail)}
              onMouseEnter={isFlyoutHost ? openChatFlyout : undefined}
              onMouseLeave={isFlyoutHost ? closeChatFlyout : undefined}
            >
              <rail.Icon />
            </button>
          );
        })}
        <div className="spacer" />
        <button
          type="button"
          className={`rail-btn ${groupId === "settings" ? "on" : ""}`}
          title="Settings"
          onClick={() => {
            setChatMenuArmed(false);
            setChatFlyoutOpen(false);
            navigate("/settings");
          }}
        >
          <IconGear />
        </button>
        <button
          type="button"
          className="avatar"
          title="Profile"
          onClick={() => {
            setChatMenuArmed(false);
            setChatFlyoutOpen(false);
            navigate("/profile");
          }}
        >
          {userInitial}
        </button>
      </nav>

      {panelOpen && (
        <div
          className="ping-overlay lg:hidden"
          onClick={() => {
            setPanelOpen(false);
            setChatFlyoutOpen(false);
            setChatMenuArmed(false);
          }}
        />
      )}

      <aside
        className={`ping-panel${panelOpen ? " open" : ""}${phonesOpen ? " phones-open" : ""}${panelFlyoutMode ? " flyout" : ""}${panelFlyoutMode && chatFlyoutOpen ? " flyout-open" : ""}`}
        onMouseEnter={() => {
          if (chatFlyoutOpen) openChatFlyout();
        }}
        onMouseLeave={closeChatFlyout}
      >
        <div className={`ping-nav-stage${navReady ? " ready" : ""}`}>
          <div className={`ping-nav-track${phonesOpen ? " phones" : ""}`}>
            <div className="ping-nav-pane pane-tabs">
              <div ref={acctRef} style={{ position: "relative" }}>
                <button type="button" className="ping-acct" onClick={() => setAcctOpen((v) => !v)}>
                  {selected?.phoneId && selected?.profilePictureUrl ? (
                    <PhoneProfileAvatar
                      phoneId={selected.phoneId}
                      profilePictureUrl={selected.profilePictureUrl}
                      sizeClass="w-[30px] h-[30px]"
                      className="ping-acct-photo"
                    />
                  ) : (
                    <div className="dot">{initials(displayName)}</div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <b className="truncate">{displayName}</b>
                    <span className="truncate">{displayNumber}</span>
                  </div>
                  <IconSwap size={14} />
                </button>
                {acctOpen && (
                  <div className="ping-acct-menu">
                    {phoneAccounts.length === 0 ? (
                      <button type="button" onClick={() => { setAcctOpen(false); navigate("/waba?connect=1"); }}>
                        <b>Connect WhatsApp</b>
                        <span>No numbers yet</span>
                      </button>
                    ) : (
                      phoneAccounts.map((acc) => (
                        <button
                          key={acc.phoneId}
                          type="button"
                          className={acc.phoneId === selectedPhoneId ? "on" : ""}
                          onClick={() => {
                            setSelectedPhoneId(acc.phoneId);
                            window.dispatchEvent(new CustomEvent("wabaAccountChanged", { detail: acc }));
                            setAcctOpen(false);
                          }}
                        >
                          <b>{acc.phoneName || acc.wabaName || "Account"}</b>
                          <span>{acc.phoneNumber}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="ping-nav">
                {visibleItems.map((item, idx) => {
                  if (item.type === "label") {
                    return <div key={`l-${item.text}-${idx}`} className="ping-nav-label">{item.text}</div>;
                  }
                  if (item.type === "logout") {
                    return (
                      <a
                        key="logout"
                        href="#logout"
                        onClick={(e) => {
                          e.preventDefault();
                          logout();
                        }}
                      >
                        <IconLogout />
                        Logout
                      </a>
                    );
                  }
                  const Icon = ICONS[item.icon] || IconGrid;
                  const active = itemMatches(location.pathname, location.search, item);
                  const isPhoneNumbers = item.path === "/waba";
                  return (
                    <Link
                      key={`${item.path}-${item.label}`}
                      to={item.path}
                      className={`${active ? "on" : ""}${item.highlight ? " subscribe-cta" : ""}`}
                      onClick={(e) => {
                        if (isPhoneNumbers) {
                          openPhoneNumbers(e);
                          return;
                        }
                        setPreferredGroup(chatFlyoutOpen ? "chat" : groupId);
                        setPanelOpen(false);
                        setChatFlyoutOpen(false);
                      }}
                    >
                      <Icon />
                      {item.label}
                      {item.badge === "unread" && unread > 0 && <span className="badge">{unread > 99 ? "99+" : unread}</span>}
                      {item.role && <span className="rolechip">{item.role}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="ping-nav-pane pane-phones">
              <button type="button" className="ping-nav-back" onClick={closePhoneNumbers}>
                <IconBack />
                Back
              </button>
              <div className="ping-phones-head">
                <div>
                  <h3>Your numbers</h3>
                  <p>Open a profile to manage it</p>
                </div>
                <span className="count">{phoneAccounts.length}</span>
              </div>
              <button
                type="button"
                className="ping-phones-add"
                onClick={addNumberFromPane}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add number
              </button>
              {phoneAccounts.length === 0 ? (
                <div className="ping-phone-empty">
                  No WhatsApp numbers yet.
                </div>
              ) : (
                <div className="ping-phone-list">
                  {phoneAccounts.map((acc) => {
                    const active = acc.phoneId === (phoneDetailId || selectedPhoneId);
                    const qTone = qualityChipTone(acc.qualityRating);
                    const limit = formatMessagingLimit(acc.messagingLimit);
                    return (
                      <button
                        key={acc.phoneId}
                        type="button"
                        className={`ping-phone-item${active ? " on" : ""}`}
                        onClick={() => selectNumber(acc, { closePanel: true })}
                      >
                        <div className="avatar-wrap">
                          {phonesOpen ? (
                            <PhoneProfileAvatar
                              phoneId={acc.phoneId}
                              profilePictureUrl={acc.profilePictureUrl}
                              sizeClass="w-8 h-8"
                              className="ping-phone-avatar"
                            />
                          ) : (
                            <div className="avatar-ph" />
                          )}
                          <i className={`live ${qTone}`} />
                        </div>
                        <div className="meta">
                          <b>{acc.phoneName || acc.wabaName || "WhatsApp number"}</b>
                          <span className="num">{acc.phoneNumber}</span>
                          <div className="stats">
                            <span className={`ping-chip ${qTone}`}>
                              {formatQualityRating(acc.qualityRating)}
                            </span>
                            {limit && limit !== "—" && (
                              <span className="limit">{limit} limit</span>
                            )}
                          </div>
                        </div>
                        {active && <em>Viewing</em>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
      <SubscribeRequiredPrompt
        open={subscribePromptOpen}
        onClose={() => setSubscribePromptOpen(false)}
      />
    </>
  );
};

export default AppNav;
